import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../../context/ThemeContext';

const PlatformSection = ({ language }) => {
    const fr = language === 'fr';
    const lang = fr ? 'fr' : 'en';
    const { theme } = useTheme();
    const themeStr = theme === 'dark' ? 'dark' : 'light';

    const content = {
        kicker: fr ? 'COURS & EXAMENS' : 'COURSES & EXAMS',
        titleDim: fr ? 'Une plateforme conçue' : 'A platform designed',
        titleBright: fr ? ' pour t’aider' : ' to help you',
        blocks: [
            {
                key: 'courses',
                icon: '/img/landing/platform_icon_courses.svg',
                title: fr ? 'Comprendre en profondeur' : 'Understand in depth',
                body: fr
                    ? 'Nos cours et examens sont conçus pour t’aider à maîtriser les concepts et aller à l’essentiel. Tu n’es plus dépendant des questions, tu réponds avec confiance.'
                    : 'Our courses and exams are built to make the concepts click, not just stick. You stop chasing questions and start answering with real confidence, because you actually understand the “why” behind every answer.',
                image: `/img/landing/platform_courses_${lang}_${themeStr}.webp`,
            },
            {
                key: 'exams',
                icon: '/img/landing/platform_icon_exams.svg',
                title: fr ? 'Des contenus à jour' : 'Up-to-date content',
                body: fr
                    ? 'Conformes aux exigences de Transports Canada pour t’entraîner sur des bases solides, sans mauvaise surprise le jour J.'
                    : 'Aligned with the latest Transport Canada requirements, so you train on solid ground and walk into exam day with no surprises.',
                image: `/img/landing/platform_exams_${lang}_${themeStr}.webp`,
            },
            {
                key: 'team',
                icon: '/img/landing/platform_icon_team.svg',
                title: fr ? 'Une équipe derrière toi' : 'A team behind you',
                body: fr
                    ? 'Un blocage ? Nos instructeurs qualifiés sont là pour comprendre ta situation et t’aider à avancer. Tu n’es jamais seul face à une difficulté.'
                    : 'When you hit a wall, you talk to qualified instructors who know exactly what you’re going through and help you push past it. You’re never stuck on your own.',
                image: `/img/landing/platform_team_${lang}_${themeStr}.webp`,
            },
        ],
    };

    // === DESKTOP: Cardless pattern — sticky image + viewport-level IntersectionObserver ===
    const [activeIndex, setActiveIndex] = useState(0);
    const blockRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = blockRefs.current.indexOf(entry.target);
                        if (idx !== -1) setActiveIndex(idx);
                    }
                });
            },
            { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        );
        blockRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);

    // === MOBILE: horizontal carousel ===
    const [mobileActive, setMobileActive] = useState(0);
    const mobileScrollRef = useRef(null);
    const mobileCardRefs = useRef([]);

    useEffect(() => {
        const root = mobileScrollRef.current;
        if (!root) return;
        const observer = new IntersectionObserver(
            (entries) => {
                let best = null;
                entries.forEach((entry) => {
                    if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
                });
                if (best && best.isIntersecting) {
                    const idx = mobileCardRefs.current.indexOf(best.target);
                    if (idx !== -1) setMobileActive(idx);
                }
            },
            { root, threshold: [0.5, 0.75, 1] }
        );
        mobileCardRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);

    const scrollToMobileCard = (idx) => {
        const card = mobileCardRefs.current[idx];
        if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    const renderHeader = (variant) => (
        <div>
            <p
                className={`font-['Rethink_Sans',sans-serif] font-bold text-[#71a7ff] ${
                    variant === 'mobile' ? 'text-[13px] tracking-[3px] leading-[24px]' : 'text-[14px] tracking-[3.5px] leading-[34px]'
                }`}
            >
                {content.kicker}
            </p>
            <h2
                className={`font-['Rethink_Sans',sans-serif] font-normal mt-3 ${
                    variant === 'mobile'
                        ? 'text-[40px] leading-[40px] tracking-[-1px]'
                        : 'text-[50px] leading-[46px] tracking-[-1.5px] mt-4'
                }`}
            >
                <span className="text-gray-400 dark:text-white/40">{content.titleDim}</span>
                <span className="text-gray-900 dark:text-white">{content.titleBright}</span>
            </h2>
        </div>
    );

    return (
        <section className="relative bg-white dark:bg-[#0f0f0f] text-gray-900 dark:text-white">
            {/* ================= MOBILE: horizontal carousel ================= */}
            <div className="md:hidden max-w-[1151px] mx-auto px-6 py-16">
                <div className="mb-10">{renderHeader('mobile')}</div>

                <div className="-mx-6">
                    <div
                        ref={mobileScrollRef}
                        className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-6 pb-4 no-scrollbar"
                    >
                        {content.blocks.map((block, i) => (
                            <div
                                key={block.key}
                                ref={(el) => (mobileCardRefs.current[i] = el)}
                                className="snap-center shrink-0 w-[82%] max-w-[340px] flex flex-col gap-5"
                            >
                                <div className="aspect-[505/587] w-full rounded-[20px] overflow-hidden bg-gray-100 dark:bg-white/5">
                                    <img
                                        src={block.image}
                                        alt={block.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <img src={block.icon} alt="" className="w-[52px] h-[52px]" />
                                <h3 className="font-['Rethink_Sans',sans-serif] font-semibold text-[20px] leading-[26px] text-gray-900 dark:text-white">
                                    {block.title}
                                </h3>
                                <p className="font-['Rethink_Sans',sans-serif] font-normal text-[15px] leading-[24px] text-gray-600 dark:text-[#a9b1bd]">
                                    {block.body}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-center gap-2 mt-4">
                        {content.blocks.map((block, i) => (
                            <button
                                key={block.key}
                                type="button"
                                aria-label={`Go to ${block.title}`}
                                onClick={() => scrollToMobileCard(i)}
                                className={`transition-all duration-300 rounded-full ${
                                    i === mobileActive ? 'w-6 h-2 bg-gray-900 dark:bg-white' : 'w-2 h-2 bg-gray-300 dark:bg-white/30 hover:bg-gray-400 dark:hover:bg-white/50'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* ================= DESKTOP: Cardless pattern — sticky image + scrolling right column ================= */}
            <div className="hidden md:block max-w-[1151px] mx-auto px-6 py-24">
                <div className="grid grid-cols-[505px_521px] gap-[125px]">
                    {/* Left grid cell stretches to grid row height (right column tall content).
                        Inline style for sticky: bypass Tailwind quirks with calc() in arbitrary values. */}
                    <div>
                        <div style={{ position: 'sticky', top: 'calc(50vh - 293px)' }}>
                            <div className="relative w-[505px] h-[587px] rounded-[20px] overflow-hidden">
                                {content.blocks.map((block, i) => (
                                    <img
                                        key={block.key}
                                        src={block.image}
                                        alt={block.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out"
                                        style={{ opacity: i === activeIndex ? 1 : 0 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: header + 3 blocks, naturally tall so each "owns" the viewport when active.
                        Block height + bottom margin ≈ 1 viewport-worth of scroll. */}
                    <div>
                        <div>{renderHeader('desktop')}</div>

                        <div className="flex flex-col">
                            {content.blocks.map((block, i) => (
                                <div
                                    key={block.key}
                                    ref={(el) => (blockRefs.current[i] = el)}
                                    className={`flex flex-col gap-[27px] py-[140px] ${
                                        i > 0 ? 'border-t border-gray-200 dark:border-white/10' : ''
                                    }`}
                                >
                                    <img src={block.icon} alt="" className="w-[62px] h-[62px]" />
                                    <h3 className="font-['Rethink_Sans',sans-serif] font-semibold text-[25px] leading-[31px] text-gray-900 dark:text-white">
                                        {block.title}
                                    </h3>
                                    <p className="font-['Rethink_Sans',sans-serif] font-normal text-[20px] leading-[31px] text-gray-600 dark:text-[#a9b1bd]">
                                        {block.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PlatformSection;
